import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, MoveUpRight, X } from "lucide-react";
import { SERVICES } from "../data/content";
import { useInquiry } from "./Inquiry";
import { EASE } from "./Motion";

export function LogoMark({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" className="logo-mark" aria-hidden="true">
      <circle cx="32" cy="32" r="26" fill="none" stroke="currentColor" strokeWidth="6" />
      <path d="M32 6 A26 26 0 0 1 32 58 Z" fill="currentColor" />
    </svg>
  );
}

export default function Header() {
  const openInquiry = useInquiry();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSvcOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) => (isActive ? "active" : "");

  return (
    <>
      <nav className={`nav ${scrolled ? "scrolled" : ""}`} data-testid="site-navigation">
        <Link to="/" className="brand" data-testid="brand-link">
          <img src="/logo-oasis-compact.png" alt="Oasis Business Solutions" className="brand-logo" />
        </Link>
        <div className="nav-links">
          <NavLink to="/" end className={linkClass} data-testid="nav-home-link">
            Home
          </NavLink>
          <div className="has-dropdown">
            <NavLink to="/services" className={linkClass} data-testid="nav-services-link">
              Services <ChevronDown size={12} />
            </NavLink>
            <div className="dropdown" data-testid="services-dropdown">
              {SERVICES.map((s, i) => (
                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`dropdown-service-${s.slug}`}>
                  <span className="dd-num">0{i + 1}</span>
                  <span>
                    <strong>{s.name}</strong>
                    <em>{s.short}</em>
                  </span>
                </Link>
              ))}
            </div>
          </div>
          <NavLink to="/work" className={linkClass} data-testid="nav-work-link">
            Work
          </NavLink>
          <NavLink to="/journal" className={linkClass} data-testid="nav-journal-link">
            Journal
          </NavLink>
          <NavLink to="/about" className={linkClass} data-testid="nav-about-link">
            About
          </NavLink>
          <NavLink to="/contact" className={linkClass} data-testid="nav-contact-link">
            Contact
          </NavLink>
          <button className="nav-cta" onClick={openInquiry} data-testid="nav-inquiry-button">
            Start a project <ArrowUpRight size={15} />
          </button>
        </div>
        <button
          className="menu-button"
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="mobile-menu-button"
          aria-label="Toggle navigation"
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-menu"
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <NavLink to="/" end className="mm-link" data-testid="mm-home-link">
              Home <MoveUpRight size={20} />
            </NavLink>
            <button className="mm-link mm-toggle" onClick={() => setSvcOpen(!svcOpen)} data-testid="mm-services-toggle">
              Services <ChevronDown size={20} style={{ transform: svcOpen ? "rotate(180deg)" : "none", transition: "transform .3s" }} />
            </button>
            <AnimatePresence initial={false}>
              {svcOpen && (
                <motion.div
                  className="mm-sub"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: EASE }}
                >
                  <Link to="/services" className="mm-sub-link" data-testid="mm-services-all">
                    All services
                  </Link>
                  {SERVICES.map((s) => (
                    <Link key={s.slug} to={`/services/${s.slug}`} className="mm-sub-link" data-testid={`mm-service-${s.slug}`}>
                      {s.name}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            <NavLink to="/work" className="mm-link" data-testid="mm-work-link">
              Work <MoveUpRight size={20} />
            </NavLink>
            <NavLink to="/journal" className="mm-link" data-testid="mm-journal-link">
              Journal <MoveUpRight size={20} />
            </NavLink>
            <NavLink to="/about" className="mm-link" data-testid="mm-about-link">
              About <MoveUpRight size={20} />
            </NavLink>
            <NavLink to="/contact" className="mm-link" data-testid="mm-contact-link">
              Contact <MoveUpRight size={20} />
            </NavLink>
            <button className="primary-button mm-cta" onClick={openInquiry} data-testid="mm-inquiry-button">
              Start a project <ArrowUpRight size={17} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
